import { useCallback, useState } from 'react'

import {
  useSetVisitorLocationMutation,
  VisitorOriginDocument,
} from './useQueries'

type ShareLocationState = {
  shareLocation: () => void
  sharing: boolean
  error: string | null
}

// Asks for the device position and shares it as the visitor's search origin.
const useShareLocation = (): ShareLocationState => {
  const [sharing, setSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [setVisitorLocation] = useSetVisitorLocationMutation({
    update: (cache, { data }) => {
      const origin = data?.setVisitorLocation?.origin
      if (!origin) return

      cache.writeQuery({
        query: VisitorOriginDocument,
        data: {
          driveTimes: { __typename: 'DriveTimes', visitorOrigin: origin },
        },
      })
    },
    // The origin changed server-side but search variables didn't, so anything
    // origin-dependent must be re-asked explicitly
    refetchQueries: [
      'driveTimeFilter',
      'listingDriveTimes',
      'search',
      'mapResults',
    ],
  })

  const shareLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Location sharing is not supported by this browser')
      return
    }

    setSharing(true)
    setError(null)

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setVisitorLocation({
          variables: { latitude: coords.latitude, longitude: coords.longitude },
        })
          .catch(() => setError('Location could not be shared'))
          .finally(() => setSharing(false))
      },
      () => {
        setError('Location could not be determined')
        setSharing(false)
      },
    )
  }, [setVisitorLocation])

  return { shareLocation, sharing, error }
}

export default useShareLocation
